// WorkspaceTour — first-visit guided tour for /workspace.
//
// Design choices (deliberate to avoid tech debt):
//   - Anchored via `data-tour-id` attributes, NOT CSS selectors. A
//     refactor that renames a className will not silently break the
//     tour. The data attribute is the explicit, stable contract.
//   - Custom DOM (no react-joyride / driver.js) — saves ~30 KB and a
//     parallel portal/event system for a 4-step affair.
//   - Single localStorage flag `propwise.workspaceTour.completed`.
//     Skip === Done. We do NOT persist partial progress — one less
//     state to reason about, and re-running the tour is cheap.
//   - Tour is gated on the welcome banner being dismissed
//     (`propwise.workspaceWelcome.dismissed`). Stacking the welcome
//     strip + tour is visually noisy and was an explicit "no" in the
//     plan.
//   - Re-positions on resize + scroll (capture) + a rAF loop while
//     open, so navigating modules keeps the spotlight aligned even
//     if the underlying button re-renders. Cheap (one rect read per
//     frame, only while the tour is open).
//   - Steps with a missing anchor auto-advance — a refactor that
//     drops an anchor degrades the tour to fewer steps rather than
//     leaving it stuck on a missing target.
//
// Failure modes considered:
//   - SSR: bail to `done=true` if window/document are missing.
//   - localStorage disabled (private mode): fall back to in-memory
//     state for the session.
//   - Anchor not yet mounted on first frame: armed flag delays the
//     tour by 600 ms, and the rAF loop keeps re-measuring.

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { TOUR_ANCHORS } from "@/lib/tour-anchors";
import { track } from "@/lib/analytics";
import { STORAGE_ONBOARDING } from "@/constants/storage";

const LS_TOUR_KEY = STORAGE_ONBOARDING.workspaceTourCompleted;
const LS_WELCOME_DISMISSED_KEY = STORAGE_ONBOARDING.workspaceWelcomeDismissed;

interface Step {
  /** data-tour-id of anchor element. Empty string = centered intro card. */
  anchor: string;
  titleKey: string;
  bodyKey: string;
}

const STEPS: Step[] = [
  { anchor: "",                                          titleKey: "workspace.tour.s1.title",         bodyKey: "workspace.tour.s1.body" },
  // Seeded-candidates CTA. If the user has no seeded properties yet (e.g.
  // dataengine miss, prefs skipped), the anchor won't render and the rAF
  // loop auto-advances after ~2s — see the missingFrames > 120 path.
  { anchor: TOUR_ANCHORS.workspaceOverviewCandidatesCta, titleKey: "workspace.tour.candidates.title", bodyKey: "workspace.tour.candidates.body" },
  { anchor: TOUR_ANCHORS.workspaceNavShortlist,          titleKey: "workspace.tour.s2.title",         bodyKey: "workspace.tour.s2.body" },
  { anchor: TOUR_ANCHORS.workspaceNavCompare,            titleKey: "workspace.tour.s3.title",         bodyKey: "workspace.tour.s3.body" },
  { anchor: TOUR_ANCHORS.workspaceNavDecision,           titleKey: "workspace.tour.s4.title",         bodyKey: "workspace.tour.s4.body" },
  // B1: cross-link to AI Advisor — surfaces the relationship between the
  // workspace (data) and /chat (analysis). Anchor is decorated on the
  // sidebar's "Open advisor" link/button. If the anchor doesn't render
  // (legacy nav variant), the rAF loop auto-advances and the tour ends
  // gracefully — see the missingFrames > 120 path.
  { anchor: TOUR_ANCHORS.workspaceAdvisorEntry,          titleKey: "workspace.tour.s5.title",         bodyKey: "workspace.tour.s5.body" },
];

interface Rect { top: number; left: number; width: number; height: number }

function getRect(anchor: string): Rect | null {
  if (typeof document === "undefined" || !anchor) return null;
  // CSS.escape covers dots/colons in our IDs (e.g. workspace.nav.shortlist)
  const sel = `[data-tour-id="${(window.CSS && CSS.escape) ? CSS.escape(anchor) : anchor}"]`;
  const el = document.querySelector(sel);
  if (!el) return null;
  const r = (el as HTMLElement).getBoundingClientRect();
  if (r.width === 0 && r.height === 0) return null;
  return { top: r.top, left: r.left, width: r.width, height: r.height };
}

export function WorkspaceTour() {
  const { t } = useTranslation();
  const [done, setDone] = useState<boolean>(() => {
    if (typeof window === "undefined") return true;
    try {
      return window.localStorage.getItem(LS_TOUR_KEY) === "1";
    } catch {
      return true; // localStorage unavailable → treat as already done
    }
  });
  const [stepIdx, setStepIdx] = useState(0);
  const [rect, setRect] = useState<Rect | null>(null);
  const rafId = useRef<number | null>(null);

  // Gate on welcome being dismissed first. Re-poll once a second so we
  // pick up the dismiss without prop-drilling. (Cheap: a string read.)
  const [welcomeDismissed, setWelcomeDismissed] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    try {
      return window.localStorage.getItem(LS_WELCOME_DISMISSED_KEY) === "1";
    } catch {
      return false;
    }
  });
  useEffect(() => {
    if (welcomeDismissed || done) return;
    const id = window.setInterval(() => {
      try {
        if (window.localStorage.getItem(LS_WELCOME_DISMISSED_KEY) === "1") {
          setWelcomeDismissed(true);
        }
      } catch {
        // ignore
      }
    }, 800);
    return () => window.clearInterval(id);
  }, [welcomeDismissed, done]);

  // Defer first-paint by 600 ms — gives the workspace nav a chance to
  // mount before we measure. Without this we race against React's
  // first commit and may show the tour anchored at (0,0).
  const [armed, setArmed] = useState(false);
  useEffect(() => {
    if (done || !welcomeDismissed) return;
    const id = window.setTimeout(() => setArmed(true), 600);
    return () => window.clearTimeout(id);
  }, [done, welcomeDismissed]);

  const step = STEPS[stepIdx];

  // Position tracking via rAF. Cheap because it only runs while the
  // tour is open and the body of the loop is a single querySelector +
  // getBoundingClientRect.
  useLayoutEffect(() => {
    if (done || !armed || !step) return;
    if (!step.anchor) {
      setRect(null);
      return;
    }
    let cancelled = false;
    let missingFrames = 0;
    const tick = () => {
      if (cancelled) return;
      const r = getRect(step.anchor);
      if (r) {
        missingFrames = 0;
        setRect((prev) => {
          if (
            prev &&
            prev.top === r.top && prev.left === r.left &&
            prev.width === r.width && prev.height === r.height
          ) {
            return prev;
          }
          return r;
        });
      } else {
        missingFrames++;
        // ~120 frames ≈ 2 s. If anchor never appears, auto-advance so
        // the user isn't stuck staring at a centered tooltip with no
        // spotlight. On the LAST step we'd be stuck on an off-screen
        // tooltip — gracefully end the tour instead.
        if (missingFrames > 120) {
          cancelled = true;
          setStepIdx((i) => {
            if (i < STEPS.length - 1) return i + 1;
            // Last step + missing anchor → finish tour rather than
            // leaving the user staring at nothing.
            try { window.localStorage.setItem(LS_TOUR_KEY, "1"); } catch { /* ignore */ }
            setDone(true);
            return i;
          });
          return;
        }
      }
      rafId.current = window.requestAnimationFrame(tick);
    };
    tick();
    return () => {
      cancelled = true;
      if (rafId.current != null) {
        window.cancelAnimationFrame(rafId.current);
        rafId.current = null;
      }
    };
  }, [done, armed, stepIdx, step]);

  const finish = () => {
    setDone(true);
    track(stepIdx >= STEPS.length - 1 ? "tour_completed" : "tour_skipped", { step: stepIdx });
    try { window.localStorage.setItem(LS_TOUR_KEY, "1"); } catch { /* private mode */ }
  };

  // Esc closes the tour. ARIA APG recommends this for role="dialog"
  // aria-modal="true". Listener attached at the document level so it
  // works regardless of where focus currently lives — the tour backdrop
  // already prevents pointer escape, but keyboard users need parity.
  useEffect(() => {
    if (done || !welcomeDismissed || !armed) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        finish();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
    // finish is stable enough — re-binding once per (done, armed, welcome) flip is fine.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [done, welcomeDismissed, armed]);

  if (done || !welcomeDismissed || !armed || !step) return null;
  if (typeof document === "undefined") return null;
  const next = () => {
    if (stepIdx < STEPS.length - 1) setStepIdx(stepIdx + 1);
    else finish();
  };
  const back = () => { if (stepIdx > 0) setStepIdx(stepIdx - 1); };

  const isCenter = !step.anchor;
  // Tooltip placement: prefer "right of anchor". If the anchor sits
  // too close to the right edge, flip to "left". This handles the
  // collapsed sidebar case where buttons are at x≈0 (right side has
  // plenty of room).
  let tooltipStyle: React.CSSProperties = {};
  if (isCenter) {
    tooltipStyle = { top: "50%", left: "50%", transform: "translate(-50%, -50%)" };
  } else if (rect) {
    const TOOLTIP_W = 320;
    const margin = 12;
    const vw = typeof window !== "undefined" ? window.innerWidth : 1024;
    const wantLeft = rect.left + rect.width + margin;
    const fitsRight = wantLeft + TOOLTIP_W + 16 <= vw;
    const left = fitsRight ? wantLeft : Math.max(16, rect.left - TOOLTIP_W - margin);
    const top = Math.max(16, Math.min(rect.top, (typeof window !== "undefined" ? window.innerHeight : 768) - 200));
    tooltipStyle = { top, left, maxWidth: TOOLTIP_W };
  } else {
    // Waiting for first measurement — render the tooltip off-screen
    // rather than at (0,0) so there's no flash.
    tooltipStyle = { top: -9999, left: -9999 };
  }

  return createPortal(
    <div
      data-tour-overlay
      style={{ position: "fixed", inset: 0, zIndex: 9999, pointerEvents: "none" }}
    >
      {/* Backdrop. Click-through to "skip" feels too aggressive — we
          require an explicit Skip/× click instead. */}
      <div
        style={{
          position: "absolute", inset: 0,
          background: "rgba(0,0,0,0.45)",
          pointerEvents: "auto",
        }}
        aria-hidden
      />
      {/* Spotlight ring */}
      {!isCenter && rect && (
        <div
          style={{
            position: "fixed",
            top: rect.top - 4, left: rect.left - 4,
            width: rect.width + 8, height: rect.height + 8,
            border: "2px solid var(--accent, #10b981)",
            borderRadius: 8,
            boxShadow: "0 0 0 9999px rgba(0,0,0,0.45)",
            pointerEvents: "none",
            transition: "top 120ms ease, left 120ms ease, width 120ms ease, height 120ms ease",
          }}
        />
      )}
      {/* Tooltip card */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t(step.titleKey)}
        style={{
          position: "fixed",
          ...tooltipStyle,
          zIndex: 10000,
          pointerEvents: "auto",
          background: "var(--surface, #fff)",
          color: "var(--ink-1, #111)",
          border: "1px solid var(--rule, rgba(0,0,0,0.08))",
          borderRadius: 12,
          padding: "14px 16px",
          boxShadow: "0 12px 36px rgba(0,0,0,0.25)",
          fontFamily: "var(--font-ui, system-ui, sans-serif)",
          minWidth: 260,
          maxWidth: 360,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 6 }}>
          <div style={{ fontSize: 11, color: "var(--ink-4, #888)", letterSpacing: "0.08em", textTransform: "uppercase" }}>
            {t("workspace.tour.stepLabel", { current: stepIdx + 1, total: STEPS.length })}
          </div>
          <button
            onClick={finish}
            aria-label={t("common.close")}
            style={{
              background: "transparent", border: "none", cursor: "pointer",
              color: "var(--ink-3, #888)", fontSize: 18, lineHeight: 1, padding: 2,
            }}
          >×</button>
        </div>
        <div style={{ fontSize: 15, fontWeight: 600, lineHeight: 1.3, marginBottom: 6 }}>
          {t(step.titleKey)}
        </div>
        <div style={{ fontSize: 13, color: "var(--ink-2, #444)", lineHeight: 1.5, marginBottom: 12 }}>
          {t(step.bodyKey)}
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
          <button
            onClick={finish}
            style={{ background: "transparent", border: "none", cursor: "pointer", fontSize: 12, color: "var(--ink-3, #888)" }}
          >{t("workspace.tour.skip")}</button>
          <div style={{ display: "flex", gap: 8 }}>
            {stepIdx > 0 && (
              <button
                onClick={back}
                style={{
                  padding: "6px 10px", borderRadius: 6,
                  border: "1px solid var(--rule, #ddd)",
                  background: "var(--surface, #fff)", cursor: "pointer", fontSize: 12,
                  color: "var(--ink-1, #111)",
                }}
              >{t("common.back")}</button>
            )}
            <button
              onClick={next}
              style={{
                padding: "6px 12px", borderRadius: 6, border: "none",
                background: "var(--accent, #10b981)", color: "#fff",
                cursor: "pointer", fontSize: 12, fontWeight: 600,
              }}
            >{stepIdx < STEPS.length - 1 ? t("workspace.tour.next") : t("workspace.tour.done")}</button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
